import { loadScript } from 'widgets/toolbox/util';
import { submitFormJson } from 'widgets/toolbox/ajax';
import { getJSONByUrl } from 'widgets/toolbox/ajax';

export type TCheckoutcomPaymentSession = {
    [key: string]: unknown;
};

export type TCheckoutcomSubmitData = {
    session_data: string;
};

export type TCheckoutcomPaymentResponse = {
    id: string;
    status: string;
    type: string;
    [key: string]: unknown;
};

type TCheckoutcomSubmitResponse = {
    success?: boolean;
    redirectUrl?: string;
    successUrl?: string;
    paymentResponse?: TCheckoutcomPaymentResponse;
};

type TCheckoutcomTranslations = Record<string, Record<string, string>>;

type TCheckoutcomPaymentComponent = {
    isAvailable: () => Promise<boolean>;
    mount: (selector: string) => void;
    unmount?: () => void;
    submit: () => void;
    isValid?: () => boolean;
};

type TCheckoutcomInstance = {
    create: {
        (componentName: string): TCheckoutcomPaymentComponent;
    };
};

export type TCheckoutcomConfig = {
    publicKey: string;
    environment: string;
    locale: string;
    paymentSession: TCheckoutcomPaymentSession;
    translations?: TCheckoutcomTranslations;
    showPayButton: boolean;
    onReady: () => void;
    onChange: () => void;
    handleClick?: (_component: unknown) => Promise<{ continue: boolean }>;
    handleSubmit: (_component: unknown, submitData: TCheckoutcomSubmitData) => Promise<TCheckoutcomPaymentResponse | void>;
    onPaymentCompleted: (_component: unknown, paymentResponse: { id?: string }) => void;
    onError: (_component: unknown, error: unknown) => void;
};

type TCheckoutcomFactory = (config: TCheckoutcomConfig) => Promise<TCheckoutcomInstance>;

type TCheckoutcomFactoryWithAvailability = TCheckoutcomFactory & {
    isAvailable: (componentName: string) => boolean;
};

declare global {
    interface Window {
        CheckoutWebComponents?: TCheckoutcomFactory;
    }
}

/**
 * @param PaymentAccordionItem Base widget for extending
 * @returns Checkout.com accordion item widget
 */
function CheckoutcomComponentClassCreator(PaymentAccordionItem: import('widgets/checkout/PaymentAccordionItem').TPaymentAccordionItem) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class CheckoutcomComponent
     * @augments PaymentAccordionItem
     * @classdesc Mounts a Checkout.com Web Components payment component inside the checkout payment
     * accordion. Every Checkout.com payment method renders its own accordion item, and each item mounts
     * the component named by `data-component-name` (`card`, `tamara`, …) from the payment session shared
     * by all of them.
     */
    class CheckoutcomComponent extends PaymentAccordionItem {
        paymentComponent: TCheckoutcomPaymentComponent | null = null;

        /**
         * Reject handler of the promise returned by submitPayment, held for as long as a
         * submission is in flight so that failures reported by the SDK callbacks can be surfaced
         * by whoever asked for the submission.
         */
        rejectSubmission: ((error: Error) => void) | null = null;

        sessionStale: boolean = false;

        refreshedPaymentSession: TCheckoutcomPaymentSession | null = null;

        sessionRefreshRequest: Promise<void> | null = null;

        /**
         * Checkoutcom-HandleRedirect URL handed over by Checkoutcom-SubmitSession, where the shopper goes
         * once Flow reports the payment completed on the page
         */
        paymentResultUrl: string | null = null;

        prefs() {
            return {
                sdkUrl: 'https://checkout-web-components.checkout.com/index.js',
                paymentSessionUrl: '',
                componentContainerRef: 'flowContainer',
                errorMessageRef: 'generalErrorMessage',
                componentName: 'card',
                paymentSession: null,
                translations: null,
                publicKey: '',
                environment: 'sandbox',
                locale: '',
                submitSessionUrl: '',
                errorLoad: '',
                errorSubmit: '',
                errorUnavailable: '',
                errorIncomplete: '',
                errorIncompleteSubmit: '',
                ...super.prefs()
            };
        }

        /**
         * @description Initialize Checkout.com component
         * @listens "coupon.updated"
         * @listens "giftcertificate.updated"
         */
        init() {
            super.init();

            this.emit('init', this);
            this.hideError();
            this.loadSdk();
            this.onDestroy(() => this.unmountComponent());
            // promo codes, loyalty rewards and gift certificates are applied on the billing step, next to
            // a component that may already be mounted against the previous basket total
            this.eventBus().on('coupon.updated', 'onBasketTotalUpdated');
            this.eventBus().on('giftcertificate.updated', 'onBasketTotalUpdated');
        }

        /**
         * @description Drops the component mounted against the previous basket total. A payment session
         * cannot be updated, so the open item is remounted right away against one for the new total, and
         * the others get theirs once opened.
         */
        onBasketTotalUpdated() {
            this.sessionStale = true;
            this.unmountComponent();

            if (this.isPanelOpen) {
                this.initCheckoutcomComponent();
            }
        }

        /**
         * @description Unmounts the Flow component and releases the SDK instance bound to it
         */
        unmountComponent() {
            const paymentComponent = this.paymentComponent;

            this.paymentComponent = null;

            if (!paymentComponent || typeof paymentComponent.unmount !== 'function') {
                return;
            }

            try {
                paymentComponent.unmount();
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Checkout.com component unmount error:', error);
            }
        }

        /**
         * @description Handles panel open
         */
        openPanel() {
            super.openPanel();
            this.initCheckoutcomComponent();
        }

        /**
         * @description Drops the mounted component on billing step activation, so the next panel open
         * mounts it against a payment session built from the basket as it is then. The 'submission'
         * stage is left alone: the component still has to take the payment from the summary step.
         * @param stage - Checkout stage the cleanup runs for
         */
        cleanupSensitiveData(stage: string) {
            super.cleanupSensitiveData(stage);

            if (stage !== 'activation') {
                return;
            }

            this.sessionStale = true;
            this.unmountComponent();
        }

        /**
         * @description Requests the payment session for the current basket and drops the component
         * mounted with the previous one
         * @returns Promise resolved once the session was refreshed
         */
        refreshPaymentSession(): Promise<void> {
            if (this.sessionRefreshRequest) {
                return this.sessionRefreshRequest;
            }

            const paymentSessionUrl = this.prefs().paymentSessionUrl;

            if (!paymentSessionUrl) {
                this.sessionStale = false;

                return Promise.resolve();
            }

            this.sessionRefreshRequest = getJSONByUrl(paymentSessionUrl)
                .then(response => {
                    const paymentSession = (response || {}).paymentSession as TCheckoutcomPaymentSession | null;

                    if (!paymentSession) {
                        return;
                    }

                    this.refreshedPaymentSession = paymentSession;
                    this.sessionStale = false;
                    this.unmountComponent();
                })
                .catch(error => {
                    // keep the session already in place rather than leaving the component without one
                    // eslint-disable-next-line no-console
                    console.error('Checkout.com payment session refresh error:', error);
                })
                .then(() => {
                    this.sessionRefreshRequest = null;
                });

            return this.sessionRefreshRequest;
        }

        /**
         * @description Handles panel close
         */
        closePanel() {
            super.closePanel();
        }

        /**
         * @description Redirects to the Checkout.com return URL and forwards payment id for synchronous flows
         * @param baseUrl - Payment result URL returned by Checkoutcom-SubmitSession
         * @param paymentId - Checkout.com payment identifier
         */
        redirectToPaymentResult(baseUrl?: string, paymentId?: string) {
            if (!baseUrl) {
                // eslint-disable-next-line no-console
                console.error('Checkout.com payment completed without a payment result URL:', paymentId);

                return;
            }

            const redirectUrl = new URL(baseUrl, window.location.origin);

            if (paymentId) {
                redirectUrl.searchParams.set('cko-payment-id', paymentId);
                redirectUrl.searchParams.set('status', 'succeeded');
            }

            window.location.assign(redirectUrl.toString());
        }

        /**
         * @description Submits the payment through Checkoutcom-SubmitSession instead of letting Flow
         * submit it against the payment session directly. The session was created for the basket, before
         * an order existed; by the time this callback fires the order has been created by PlaceOrder, so
         * the server submits the payment under the order number as its merchant reference.
         *
         * The response body has to reach Flow unmodified, which is why the server nests it under
         * `paymentResponse` — the ajax layer adds its own keys to the top level of every response.
         * Returning nothing makes Flow abort the submission; the server has already failed the order in
         * that case and tells us where to send the shopper.
         * @param submitData - Session data token captured by Flow
         * @returns Checkout.com payment response, or nothing to abort the submission
         */
        async handleSubmit(submitData: TCheckoutcomSubmitData): Promise<TCheckoutcomPaymentResponse | void> {
            let response: TCheckoutcomSubmitResponse | undefined;

            try {
                response = (await submitFormJson(this.prefs().submitSessionUrl, {
                    session_data: submitData?.session_data
                })) as TCheckoutcomSubmitResponse;
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Checkout.com submit session error:', error);
            }

            if (response?.success && response.paymentResponse) {
                this.paymentResultUrl = response.successUrl || null;

                return response.paymentResponse;
            }

            if (response?.redirectUrl) {
                window.location.assign(response.redirectUrl);

                return undefined;
            }

            this.failSubmission(this.prefs().errorSubmit);

            return undefined;
        }

        /**
         * @description Reports a failed submission. A submission can only fail once the shopper has
         * left the payment step, where this accordion item is collapsed and its inline error would
         * go unseen, so the message is handed to the caller of `submitPayment` to render. Falls back
         * to the inline error when no submission is waiting on it, which is how load-time failures
         * are still shown on the payment step.
         * @param message - Error message to show
         */
        failSubmission(message: string) {
            const rejectSubmission = this.rejectSubmission;

            this.rejectSubmission = null;

            if (rejectSubmission) {
                rejectSubmission(new Error(message));

                return;
            }

            this.setError(message);
        }

        /**
         * @description Builds the configuration the Checkout.com Web Components SDK is initialized with
         * @param paymentSession - Payment session the component is mounted against
         * @returns SDK configuration
         */
        getCheckoutConfig(paymentSession: TCheckoutcomPaymentSession): TCheckoutcomConfig {
            const translations = this.getTranslations();

            return {
                publicKey: this.prefs().publicKey,
                environment: this.prefs().environment,
                locale: this.prefs().locale,
                paymentSession: paymentSession,
                showPayButton: false,
                ...(translations ? { translations } : {}),
                onReady: () => {
                    this.hideError();
                    this.updateState();
                },
                onChange: () => {
                    if (this.isPaymentComponentValid()) {
                        this.hideError();
                    }

                    this.updateState();
                },
                handleSubmit: (_, submitData) => this.handleSubmit(submitData),
                onPaymentCompleted: (_, paymentResponse) => {
                    this.redirectToPaymentResult(this.paymentResultUrl || undefined, paymentResponse?.id);
                },
                onError: (_, error) => {
                    this.failSubmission(this.prefs().errorLoad);
                    // Preserve visibility for integration failures without relying on inline scripts.
                    // eslint-disable-next-line no-console
                    console.error('Checkout.com error:', error);
                }
            };
        }

        /**
         * @description Initialize and mount the Checkout.com component configured for this accordion item
         * @returns Checkout.com payment component instance
         */
        async initCheckoutcomComponent(): Promise<TCheckoutcomPaymentComponent | null> {
            if (this.sessionStale) {
                await this.refreshPaymentSession();
            }

            if (this.paymentComponent) {
                return this.paymentComponent;
            }

            const paymentSession = this.getPaymentSession();

            if (!paymentSession || !this.prefs().publicKey) {
                return null;
            }

            try {
                const checkoutWebComponents = (await this.loadSdk()) as TCheckoutcomFactoryWithAvailability;

                if (!checkoutWebComponents) {
                    return null;
                }

                const checkout = await checkoutWebComponents(this.getCheckoutConfig(paymentSession));
                const selector = this.getComponentContainerSelector();

                if (!selector) {
                    return null;
                }

                const componentName = this.prefs().componentName;

                if (!componentName) {
                    throw new Error('Missing data-component-name on the Checkout.com accordion item');
                }

                const paymentComponent = checkout.create(componentName);

                if (await paymentComponent.isAvailable()) {
                    paymentComponent.mount(selector);
                    this.paymentComponent = paymentComponent;
                    this.updateState();

                    return paymentComponent;
                } else {
                    this.setError(this.prefs().errorUnavailable);
                }
            } catch (error) {
                this.setError(this.prefs().errorLoad);
                // eslint-disable-next-line no-console
                console.error('Checkout.com SDK load error:', error);
            }

            return null;
        }

        /**
         * @description Returns mounted container selector
         * @returns Container selector or null when missing
         */
        getComponentContainerSelector(): string | null {
            const container = this.ref(this.prefs().componentContainerRef).get() as HTMLElement | null;

            if (!container) {
                return null;
            }

            if (!container.id) {
                container.id = `checkoutcom-component-${this.prefs().methodId || 'payment'}`;
            }

            return `#${container.id}`;
        }

        /**
         * @description Load Checkout.com SDK
         * @returns Checkout.com SDK global factory
         */
        loadSdk() {
            if (window.CheckoutWebComponents) {
                return Promise.resolve(window.CheckoutWebComponents);
            }

            return loadScript<TCheckoutcomFactory>(this.prefs().sdkUrl, {
                globalScriptName: 'CheckoutWebComponents'
            });
        }

        /**
         * @description Returns normalized payment session data
         * @returns Checkout.com payment session
         */
        getPaymentSession(): TCheckoutcomPaymentSession | null {
            if (this.refreshedPaymentSession) {
                return this.refreshedPaymentSession;
            }

            const paymentSession = this.prefs().paymentSession;

            if (!paymentSession) {
                return null;
            }

            if (typeof paymentSession === 'string') {
                try {
                    return JSON.parse(paymentSession) as TCheckoutcomPaymentSession;
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error('Checkout.com payment session parse error:', error);

                    return null;
                }
            }

            return paymentSession as TCheckoutcomPaymentSession;
        }

        /**
         * @description Returns SDK translations map sourced from server-side resources
         * @returns Translations map or null when none configured
         */
        getTranslations(): TCheckoutcomTranslations | null {
            const translations = this.prefs().translations;

            if (!translations) {
                return null;
            }

            if (typeof translations === 'string') {
                try {
                    return JSON.parse(translations) as TCheckoutcomTranslations;
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error('Checkout.com translations parse error:', error);

                    return null;
                }
            }

            return translations as TCheckoutcomTranslations;
        }

        /**
         * @description Handles payment tab submit
         * @returns Empty submission payload
         */
        process(): Promise<Record<string, never>> {
            return Promise.resolve({});
        }

        /**
         * @description Checks whether the mounted component holds valid input. Redirect based components
         * such as Tamara collect nothing on our page and do not expose `isValid`, so a mounted component
         * without the method counts as valid.
         * @param paymentComponent - Component to validate, defaults to the mounted one
         * @returns True when the component is mounted and holds no incomplete input
         */
        isPaymentComponentValid(paymentComponent = this.paymentComponent): boolean {
            if (!paymentComponent) {
                return false;
            }

            return typeof paymentComponent.isValid === 'function' ? paymentComponent.isValid() : true;
        }

        /**
         * @description Validates the payment component before continuing to the review step
         * @returns True when the component holds complete details
         */
        isChildrenValid(): boolean {
            if (this.isPaymentComponentValid()) {
                this.hideError();

                return true;
            }

            this.setError(this.prefs().errorIncomplete);

            return false;
        }

        /**
         * @description Submits the Checkout.com payment from the summary Place Order action.
         *
         * Flow's `submit()` returns as soon as the component has handed over to `handleSubmit`, so
         * the returned promise carries the outcome of the submission instead. It only ever settles
         * by rejecting: a submission that goes through navigates away, either to the provider or to
         * the payment result page.
         * @returns Promise rejected with the message to show when the payment could not be taken
         */
        async submitPayment(): Promise<void> {
            const paymentComponent = await this.initCheckoutcomComponent();
            const errorMessage = paymentComponent ? this.prefs().errorIncompleteSubmit : this.prefs().errorLoad;

            if (!this.isPaymentComponentValid(paymentComponent)) {
                this.setError(errorMessage);
                throw new Error(errorMessage);
            }

            this.hideError();

            return new Promise<void>((_, reject) => {
                this.rejectSubmission = reject;

                try {
                    paymentComponent?.submit();
                } catch (error) {
                    this.rejectSubmission = null;
                    reject(error instanceof Error ? error : new Error(errorMessage));
                }
            });
        }

        /**
         * @description Show accordion level error
         * @param message - Error message
         */
        setError(message: string) {
            const errorMessage = this.ref(this.prefs().errorMessageRef);

            if (errorMessage.exists()) {
                errorMessage.setText(message);
                errorMessage.show();
            }

            this.eventBus().emit('checkoutcom.payment.error', message);
            this.updateState();
        }

        /**
         * @description Hide accordion level error
         */
        hideError() {
            const errorMessage = this.ref(this.prefs().errorMessageRef);

            if (errorMessage.exists()) {
                errorMessage.setText('');
                errorMessage.hide();
            }
        }
    }

    return CheckoutcomComponent;
}

export type TCheckoutcomComponent = ReturnType<typeof CheckoutcomComponentClassCreator>;
export type TCheckoutcomComponentInstance = InstanceType<TCheckoutcomComponent>;

export default CheckoutcomComponentClassCreator;
